/**
 * Template Collections API Functions
 *
 * CRUD operations for Template Collections and Runs.
 * Part of Template Collections V1.
 */

import { apiClient } from '../api-client';
import type {
  TemplateCollection,
  TemplateCollectionItem,
  BudgetInfo,
  CollectionListResponse,
  CollectionResponse,
  CollectionItemResponse,
  CreateCollectionInput,
  UpdateCollectionInput,
  AddItemInput,
  UpdateItemInput,
} from '@/types/template-collections';

// =============================================================================
// COLLECTION API
// =============================================================================

/**
 * List collections for the current workspace
 */
export async function listCollections(params?: {
  type?: string;
  page?: number;
  page_size?: number;
}): Promise<CollectionListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.type) searchParams.set('type', params.type);
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.page_size) searchParams.set('page_size', params.page_size.toString());

  const query = searchParams.toString();
  const url = `/api/v2/template-collections${query ? `?${query}` : ''}`;
  const response = await apiClient.get<CollectionListResponse>(url);
  return response.data;
}

/**
 * Get a single collection with items
 */
export async function getCollection(collectionId: string): Promise<TemplateCollection> {
  const response = await apiClient.get<CollectionResponse>(
    `/api/v2/template-collections/${collectionId}`
  );
  return response.data.collection;
}

/**
 * Create a new collection
 */
export async function createCollection(data: CreateCollectionInput): Promise<TemplateCollection> {
  const response = await apiClient.post<CollectionResponse>(
    '/api/v2/template-collections',
    data
  );
  return response.data.collection;
}

/**
 * Update a collection
 */
export async function updateCollection(
  collectionId: string,
  data: UpdateCollectionInput
): Promise<TemplateCollection> {
  const response = await apiClient.patch<CollectionResponse>(
    `/api/v2/template-collections/${collectionId}`,
    data
  );
  return response.data.collection;
}

/**
 * Delete a collection (soft delete)
 */
export async function deleteCollection(collectionId: string): Promise<void> {
  await apiClient.delete(`/api/v2/template-collections/${collectionId}`);
}

// =============================================================================
// ITEM API
// =============================================================================

/**
 * Add a template to a collection
 */
export async function addItem(
  collectionId: string,
  data: AddItemInput
): Promise<TemplateCollectionItem> {
  const response = await apiClient.post<CollectionItemResponse>(
    `/api/v2/template-collections/${collectionId}/items`,
    data
  );
  return response.data.item;
}

/**
 * Update a collection item
 */
export async function updateItem(
  collectionId: string,
  itemId: string,
  data: UpdateItemInput
): Promise<TemplateCollectionItem> {
  const response = await apiClient.patch<CollectionItemResponse>(
    `/api/v2/template-collections/${collectionId}/items/${itemId}`,
    data
  );
  return response.data.item;
}

/**
 * Remove an item from a collection
 */
export async function removeItem(collectionId: string, itemId: string): Promise<void> {
  await apiClient.delete(`/api/v2/template-collections/${collectionId}/items/${itemId}`);
}

/**
 * Reorder items in a collection
 */
export async function reorderItems(
  collectionId: string,
  itemOrders: Array<{ item_id: string; sort_order: number }>
): Promise<void> {
  await apiClient.patch(`/api/v2/template-collections/${collectionId}/reorder`, {
    item_orders: itemOrders,
  });
}

// =============================================================================
// BUDGET API
// =============================================================================

/**
 * Get budget information for a collection
 */
export async function getBudget(collectionId: string): Promise<BudgetInfo> {
  const response = await apiClient.get<BudgetInfo>(
    `/api/v2/template-collections/${collectionId}/budget`
  );
  return response.data;
}
